// components/DebugConsole.js — CONSOLE DE DEBUG EMBARQUÉE (bouton 🐞).
//
// Demande Lucas (juillet 2026) : pouvoir VOIR les logs de l'app Android et me
// les COPIER en un geste. Ce panneau lit le buffer de utils/debugLogger (console,
// erreurs, requêtes API, navigation, cycle de vie), affiche l'environnement
// (origine, natif ?, base API, cookies, service worker) et propose :
//   • « Copier tout » → rapport texte complet dans le presse-papier ;
//   • « Tester l'API » → ping /listings, /auth/me, /associations (loggés) ;
//   • « Purger cache + SW » → vide les caches + désinscrit le service worker ;
//   • « Vider » les logs.
//
// Visible dans l'app native (Capacitor) OU sur le web avec ?debug=1 (ou
// localStorage gively_debug=1). Invisible pour l'utilisateur lambda du web.
import React, { useEffect, useState, useCallback } from "react";
import { getLogs, clearLogs, subscribe, envSnapshot, pushLog } from "../utils/debugLogger";
import { api } from "../context/AuthContext";
import { API_URL } from "../config/backend";

// Opt-in EXPLICITE (?debug=1 ou flag localStorage). Décision Lucas (juillet 2026,
// release Play Store) : la coccinelle ne doit JAMAIS être visible d'un utilisateur
// lambda — avant, `if (s.isNative) return true` l'affichait à TOUS les utilisateurs
// de l'app Android. Désormais : ADMIN connecté (Lucas) OU flag debug explicite.
function explicitDebugOptIn() {
  try {
    if (typeof window !== "undefined" && /[?&]debug=1/.test(window.location.search)) {
      try { localStorage.setItem("gively_debug", "1"); } catch (_) {}
      return true;
    }
    if (typeof localStorage !== "undefined" && localStorage.getItem("gively_debug") === "1") return true;
  } catch (_) {}
  return false;
}

const LEVEL_COLOR = { error: "#DC2626", warn: "#D97706", info: "#334155" };

export default function DebugConsole() {
  // Décision Lucas (16/08/2026) : la coccinelle n'apparaît PLUS automatiquement
  // pour un admin — elle masquait des éléments du tunnel et polluait l'écran.
  // Seul l'opt-in EXPLICITE l'affiche désormais (?debug=1 dans l'URL, qui pose
  // le flag localStorage). Réactivable à la demande, y compris par Lucas.
  const visible = explicitDebugOptIn();
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    return subscribe(() => force((n) => n + 1));
  }, [visible]);

  const buildReport = useCallback(() => {
    const s = envSnapshot();
    const head =
      "=== GIVELY DEBUG (à copier-coller) ===\n" +
      `date        : ${new Date().toISOString()}\n` +
      `origin      : ${s.origin}\n` +
      `href        : ${s.href}\n` +
      `isNative    : ${s.isNative}\n` +
      `platform    : ${s.platform}\n` +
      `API base    : ${API_URL}\n` +
      `online      : ${s.online}\n` +
      `SW actif    : ${s.swControlled}\n` +
      `cookies     : ${s.cookiesPresent} présent(s)\n` +
      `buildId     : ${s.buildId}\n` +
      `userAgent   : ${s.userAgent}\n` +
      "======================================\n";
    const lines = getLogs().map((l) => `${l.t} [${l.level}] ${l.cat}: ${l.msg}`).join("\n");
    return head + lines + "\n=== FIN (" + getLogs().length + " lignes) ===";
  }, []);

  const copyAll = useCallback(async () => {
    const text = buildReport();
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (_) { /* fallback ci-dessous */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) { /* rien */ }
    }
    setCopied(ok);
    setTimeout(() => setCopied(false), 2000);
    if (!ok) pushLog("warn", "debug", "Copie presse-papier indisponible — sélection manuelle nécessaire.");
  }, [buildReport]);

  const testApi = useCallback(async () => {
    pushLog("info", "test", "--- Test API démarré ---");
    const endpoints = [
      ["GET", "/listings?limit=1", "annonces (public)"],
      ["GET", "/auth/me", "session utilisateur"],
      ["GET", "/associations", "associations"],
    ];
    for (const [, url, label] of endpoints) {
      try {
        const res = await api.get(url);
        const n = Array.isArray(res.data) ? res.data.length : (res.data ? 1 : 0);
        pushLog("info", "test", `OK ${label} → ${res.status} (${n} élément(s))`);
      } catch (err) {
        const st = err && err.response ? err.response.status : "réseau/CORS";
        pushLog("error", "test", `KO ${label} → ${st} : ${(err && err.message) || err}`);
      }
    }
    pushLog("info", "test", "--- Test API terminé ---");
  }, []);

  const purge = useCallback(async () => {
    try {
      if ("caches" in window) {
        const ks = await caches.keys();
        await Promise.all(ks.map((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const rs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(rs.map((r) => r.unregister()));
      }
      pushLog("info", "debug", "Cache + Service Worker purgés — rechargement…");
      setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 400);
    } catch (e) {
      pushLog("error", "debug", "Échec purge cache/SW: " + ((e && e.message) || e));
    }
  }, []);

  if (!visible) return null;

  const s = envSnapshot();
  const logs = getLogs();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Console de debug"
        style={{
          position: "fixed", left: 10, bottom: "calc(env(safe-area-inset-bottom, 0px) + 74px)",
          zIndex: 2147483000, width: 46, height: 46, borderRadius: 23, border: "none",
          background: "#0F172A", color: "#fff", fontSize: 20, boxShadow: "0 4px 14px rgba(0,0,0,.35)",
        }}
      >🐞</button>

      {open && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 2147483001, background: "rgba(15,23,42,.55)",
            display: "flex", flexDirection: "column", justifyContent: "flex-end",
          }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderTopLeftRadius: 16, borderTopRightRadius: 16,
              maxHeight: "82vh", display: "flex", flexDirection: "column",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
          >
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 8 }}>
              <strong style={{ fontSize: 14, color: "#0F172A", flex: 1 }}>🐞 Debug Gively</strong>
              <button onClick={() => setOpen(false)} style={btn("#E2E8F0", "#0F172A")}>✕</button>
            </div>

            <div style={{ padding: "8px 14px", fontSize: 11, color: "#475569", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", lineHeight: 1.5 }}>
              <div><b>API</b> : {API_URL}</div>
              <div><b>origin</b> : {s.origin} · <b>natif</b> : {String(s.isNative)} · <b>{s.platform}</b></div>
              <div><b>online</b> : {String(s.online)} · <b>SW</b> : {String(s.swControlled)} · <b>cookies</b> : {s.cookiesPresent}</div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 14px", borderBottom: "1px solid #E2E8F0" }}>
              <button onClick={copyAll} style={btn("#1DA1F2", "#fff")}>{copied ? "✓ Copié" : "Copier tout"}</button>
              <button onClick={testApi} style={btn("#0EA5E9", "#fff")}>Tester l'API</button>
              <button onClick={() => { clearLogs(); }} style={btn("#E2E8F0", "#0F172A")}>Vider</button>
              <button onClick={purge} style={btn("#FEE2E2", "#B91C1C")}>Purger cache + SW</button>
            </div>

            <div style={{ overflowY: "auto", padding: "8px 12px", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11, lineHeight: 1.45 }}>
              {logs.length === 0 && <div style={{ color: "#94A3B8" }}>Aucun log pour l'instant. Navigue dans l'app, puis reviens ici.</div>}
              {logs.slice().reverse().map((l, i) => (
                <div key={logs.length - i} style={{ padding: "2px 0", borderBottom: "1px solid #F1F5F9", color: LEVEL_COLOR[l.level] || "#334155", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  <span style={{ color: "#94A3B8" }}>{l.t}</span> <b>[{l.cat}]</b> {l.msg}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function btn(bg, fg) {
  return { background: bg, color: fg, border: "none", borderRadius: 10, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" };
}
